import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { empresa, servicos, avaliacoes, produtos } from '../data/mock';

const Home = () => {
  const servicosDestaque = servicos.slice(0, 4);
  const produtosDestaque = produtos.slice(0, 3);

  const handleWhatsApp = (mensagem = '') => {
    const texto = mensagem ? encodeURIComponent(mensagem) : encodeURIComponent('Olá! Gostaria de mais informações.');
    window.open(`${empresa.contato.whatsapp_link}?text=${texto}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden bg-gradient-to-br from-purple-900 via-blue-800 to-cyan-700">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1646756089735-487709743361?w=1920&h=1080&fit=crop"
            alt="Assistência Técnica"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-blue-800/90 to-cyan-800/85" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-cyan-500/20 text-cyan-200 border-cyan-400/30 hover:bg-cyan-500/30">
              <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />
              {empresa.avaliacao.nota} • {empresa.avaliacao.total} Avaliações no Google
            </Badge>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Assistência Técnica e Vendas em{' '}
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Belo Horizonte</span>
            </h1>

            <p className="text-xl opacity-90">
              Especialistas em manutenção de notebooks e computadores. Mais de 30 anos de experiência, 
              atendimento profissional e preços justos na região da Pampulha.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={() => handleWhatsApp('Olá! Gostaria de agendar uma assistência técnica.')}
              >
                <Phone className="w-5 h-5 mr-2" />
                Agendar Assistência
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all duration-300"
                onClick={() => document.getElementById('produtos').scrollIntoView({ behavior: 'smooth' })}
              >
                Ver Produtos
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap gap-8 text-gray-300">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                <span>{empresa.endereco.bairro}, BH</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                <span>Aberto até 18:00</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-cyan-400" />
                <span>{empresa.contato.telefone}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços em Destaque */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Nossos Serviços
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Soluções completas em informática com garantia e qualidade
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicosDestaque.map((servico) => (
              <Card
                key={servico.id}
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-purple-400"
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-cyan-600 transition-all">
                    <div className="text-blue-600 group-hover:text-white transition-colors text-2xl">
                      🔧
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {servico.titulo}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {servico.descricao}
                  </p>
                  <p className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent font-bold">{servico.preco}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servicos">
              <Button size="lg" variant="outline" className="border-2 border-purple-600 text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-blue-600 hover:text-white hover:border-transparent">
                Ver Todos os Serviços
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Produtos em Destaque */}
      <section id="produtos" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Notebooks e Computadores
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Produtos novos e seminovos com garantia e parcelamento facilitado
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {produtosDestaque.map((produto) => (
              <Card
                key={produto.id}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <Link to={`/produto/${produto.id}`}>
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden cursor-pointer group">
                    <img
                      src={produto.imagem}
                      alt={produto.nome}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                        Ver Detalhes
                      </span>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {produto.nome}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{produto.descricao}</p>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Badge variant="secondary" className="text-xs">
                        {produto.processador}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-xs">{produto.ram}</Badge>
                      <Badge variant="outline" className="text-xs">{produto.armazenamento}</Badge>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
                        R$ {produto.preco_vista.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <span className="text-xs text-gray-600">à vista</span>
                    </div>
                    <p className="text-xs text-gray-500">ou em até {produto.parcelas}x sem juros</p>
                  </div>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleWhatsApp(`Olá! Tenho interesse no ${produto.nome}.`)}
                    >
                      WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/loja">
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                Ver Todos os Produtos
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Avaliações */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Avaliação {empresa.avaliacao.nota} no Google
            </h2>
            <p className="text-xl text-gray-600">
              Confira o que nossos clientes dizem sobre nós
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {avaliacoes.map((avaliacao) => (
              <Card key={avaliacao.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={avaliacao.foto}
                      alt={avaliacao.nome}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">{avaliacao.nome}</p>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-2">{avaliacao.comentario}</p>
                  <p className="text-xs text-gray-500">{avaliacao.data}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-purple-700 via-blue-700 to-cyan-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Pronto para resolver seu problema?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Entre em contato agora mesmo e agende seu atendimento. Estamos prontos para ajudar!
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-purple-700 hover:bg-gray-100 px-8 py-6 text-lg shadow-lg font-bold"
              onClick={() => handleWhatsApp()}
            >
              <Phone className="w-5 h-5 mr-2" />
              Chamar no WhatsApp
            </Button>
            <Link to="/contato">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Como Chegar
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
